import React, { Component } from "react";
import { toast } from "react-toastify";
import BaseForm from "./../common/baseForm";
import SubscriptionsTable from "./../subscriptionsTable";
import BillingProfile from "./../billingProfile";
import UserParsedHouseEventsTable from "./userParsedHousesEventsTable";
import MessagesTable from "./messagesTable";
import DefaultPanelContainer from "./../common/defaultPanelContainer";
import auth from "./../../services/authService";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
} from "reactstrap";
import http from "../../services/httpService";
import PaymentsTable from "./../paymentsTable";
import SearchProfilesTable from "./../searchProfilesTable";
import UserForm from "./userForm";
import { AdminPermission, CustomerServicePermission } from "../adminPermission";
import UserCustomerServiceForm from "./userCustomerServiceForm";
import BlockedWebsitesTable from "./../blockedWebsitesTable";
import UserListingStatusesTable from "./userListingStatusesTable";

// Display order for known attribution keys. Canonical whitelist lives in
// RentSlam.Business.Core/Extensions/UserAttributionExtensions.cs
// (see UserAttributionExtensions.AttributionKeys). Keep in sync when
// adding a new attribution key; unknown keys still render, appended
// alphabetically after the known ones.
const ATTRIBUTION_KEY_ORDER = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "fbclid",
  "gclid",
  "ttclid",
  "ga_client_id",
  "ga_session_id",
  "_fbp",
  "_fbc",
  "landing_page",
  "referrer",
];

const orderAttributionKeys = (metadata) => {
  if (!metadata) return [];
  const keys = Object.keys(metadata);
  const known = ATTRIBUTION_KEY_ORDER.filter((k) => keys.includes(k));
  const extra = keys
    .filter((k) => !ATTRIBUTION_KEY_ORDER.includes(k))
    .sort();
  return [...known, ...extra];
};

// Mirrors RentSlam.Business.Core/Models/UserAttributionTouch.cs
// AttributionTouchType. Must stay aligned with the enum's byte values;
// new entries need to be appended here too.
const TOUCH_TYPE_LABELS = {
  1: "Campaign arrival",
  2: "Registration",
  3: "Login",
  4: "Conversion",
  5: "Renewal conversion",
  6: "Refund",
};

const formatTouchType = (touchType) =>
  TOUCH_TYPE_LABELS[touchType] ?? `Type ${touchType}`;

class UserDetails extends Component {
  state = {
    isLoaded: false,
    tree: [],
    billingProfile: {},
    userDetails: null,
    isErasingAttribution: false,
  };

  loadBillingProfile = async () => {
    if (this.props.match.params.id === "new") return;
    const { data } = await http.get(
      "billingprofiles/" + this.props.match.params.id
    );

    this.setState({ billingProfile: data });
  };

  loadUserDetails = async () => {
    if (this.props.match.params.id === "new") return;
    const { data } = await http.get(
      "users/edit/" + this.props.match.params.id
    );
    this.setState({ userDetails: data });
  };

  async componentDidMount() {
    await this.loadBillingProfile();
    await this.loadUserDetails();
  }

  doUnsubscribe = async (confirmationMessage, path, successMessage) => {
    if (window.confirm(confirmationMessage)) {
      await http.put(path + this.props.match.params.id);
      toast.success(successMessage);
      // await this.loadSearchProfile(data);
      await this.loadBillingProfile();

      this.setState({ loading: false });
    }
  };

  handleUnsubscribeClick = async () => {
    await this.doUnsubscribe(
      "Are you sure you want to unsubscribe this user",
      "users/unsubscribe/",
      "user sucessfully unsubscribed"
    );
  };

  handleUnsubscribeAndNotifyClick = async () => {
    await this.doUnsubscribe(
      "Are you sure you want to unsubscribe and notify this user by email",
      "users/unsubscribeandnotify/",
      "user sucessfully unsubscribed and notified"
    );
  };

  handleDeleteForeverClick = async () => {
    if (window.confirm("Are you sure. This operation cannot be undone")) {
      try {
        await http.delete("users/delete/" + this.props.match.params.id);
        toast.success("user sucessfully deleted");
      } catch (ex) {
        if (ex.response && ex.response.status === 400) {
          const errors = { ...this.state.errors };
          errors.username = ex.response.data;
          this.setState({ errors });
        }
      }
    }
  };

  handleEraseAttributionTimelineClick = async () => {
    if (this.state.isErasingAttribution) return; // double-click guard
    const count =
      this.state.userDetails?.attributionTimeline?.length ?? 0;
    const message =
      "Erase this user's attribution timeline? " +
      `${count} touch row(s) will be permanently deleted. ` +
      "The account itself will remain. This action cannot be undone.";
    if (!window.confirm(message)) return;
    this.setState({ isErasingAttribution: true });
    try {
      const response = await http.delete(
        "users/" + this.props.match.params.id + "/attribution-touches"
      );
      const deleted = response?.data ?? count;
      toast.success(`Erased ${deleted} attribution touch row(s)`);
      const userDetails = { ...this.state.userDetails, attributionTimeline: [] };
      this.setState({ userDetails });
    } catch (ex) {
      toast.error(
        ex?.response?.data?.message || "Failed to erase attribution timeline"
      );
    } finally {
      this.setState({ isErasingAttribution: false });
    }
  };

  handlePauseClick = async () => {
    if (window.confirm("Are you sure you want to pause this user's account? They will stop receiving listings.")) {
      try {
        await http.put("users/pause/" + this.props.match.params.id);
        toast.success("Account successfully paused");
        await this.loadUserDetails();
      } catch (ex) {
        const message = ex.response?.data || "Failed to pause account";
        toast.error(message);
      }
    }
  };

  handleUnpauseClick = async () => {
    if (window.confirm("Are you sure you want to unpause this user's account? Their subscription end date will be extended by the number of paused days.")) {
      try {
        await http.put("users/unpause/" + this.props.match.params.id);
        toast.success("Account successfully unpaused");
        await this.loadUserDetails();
      } catch (ex) {
        const message = ex.response?.data || "Failed to unpause account";
        toast.error(message);
      }
    }
  };

  handleToggleAutorenewals = async () => {
    if (window.confirm("do you want to toggle autorenewals?")) {
      await http.post("billingprofiles/toggle/" + this.props.match.params.id);
      await this.loadBillingProfile();
      toast.success("billing profile successfully toggled");
    }
  };

  render() {
    // if (!this.state.isLoaded) return null;
    const id = this.props.match.params.id;
    const isNewUser = id === "new";

    return (
      <Container>
        <AdminPermission user={auth.getCurrentUser()}>
          <UserForm userId={id} />
        </AdminPermission>
        <CustomerServicePermission user={auth.getCurrentUser()}>
          <UserCustomerServiceForm userId={id} />
        </CustomerServicePermission>
        {isNewUser ? null : (
          <>
            <Row>
              <Col>
                <BlockedWebsitesTable userId={id} />
              </Col>
            </Row>
            <Row>
              <Col>
                <SearchProfilesTable userId={id} />
              </Col>
            </Row>
            <Row>
              <Col>
                <DefaultPanelContainer title="Payments">
                  <PaymentsTable minRows={4} endpoint={"payments/user/" + id} />
                </DefaultPanelContainer>
              </Col>
            </Row>
            <Row>
              <Col>
                <SubscriptionsTable userId={id} />
              </Col>
            </Row>
            <Row>
              <Col>
                <BillingProfile
                  billingProfile={this.state.billingProfile}
                  // onCancel={this.handleCancelBillingProfileClick}
                  onToggleAutorenewals={this.handleToggleAutorenewals}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <UserParsedHouseEventsTable
                  title="Parsed houses"
                  getUrl={() =>
                    "userparsedhouseevent/user/" + this.props.match.params.id
                  }
                  userId={this.props.match.params.id}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <MessagesTable userId={this.props.match.params.id} />
              </Col>
            </Row>
            <Row>
              <Col>
                <UserListingStatusesTable userId={this.props.match.params.id} />
              </Col>
            </Row>
            {this.state.userDetails?.authProvider && (
              <Row className="mb-2">
                <Col>
                  <strong>Auth provider:</strong> {this.state.userDetails.authProvider}
                </Col>
              </Row>
            )}
            {this.state.userDetails?.attributionMetadata && (
              <Row>
                  <Col>
                    <Card className="my-3">
                      <CardHeader>Acquisition attribution</CardHeader>
                      <CardBody>
                        <Table size="sm" bordered>
                          <thead>
                            <tr>
                              <th scope="col" style={{ width: "25%" }}>
                                Key
                              </th>
                              <th scope="col">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderAttributionKeys(
                              this.state.userDetails.attributionMetadata
                            ).map((key) => (
                              <tr key={key}>
                                <th scope="row">{key}</th>
                                <td style={{ wordBreak: "break-word" }}>
                                  {
                                    this.state.userDetails.attributionMetadata[
                                      key
                                    ]
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            {this.state.userDetails?.attributionTimeline?.length > 0 && (
              <Row>
                <Col>
                  <Card className="my-3">
                    <CardHeader>
                      Attribution timeline ({this.state.userDetails.attributionTimeline.length})
                    </CardHeader>
                    <CardBody>
                      <Table size="sm" bordered striped>
                        <thead>
                          <tr>
                            <th scope="col" style={{ width: "16%" }}>When</th>
                            <th scope="col" style={{ width: "14%" }}>Event</th>
                            <th scope="col" style={{ width: "14%" }}>Channel</th>
                            <th scope="col" style={{ width: "20%" }}>Campaign</th>
                            <th scope="col">Page / referrer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {this.state.userDetails.attributionTimeline.map((touch) => (
                            <tr key={touch.id}>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {new Date(touch.createDate).toLocaleString()}
                              </td>
                              <td>
                                {formatTouchType(touch.touchType)}
                                {touch.anonymousId && !touch.userId && (
                                  <span
                                    className="badge bg-secondary ms-1"
                                    title="Observed before stitch"
                                  >
                                    anon
                                  </span>
                                )}
                              </td>
                              <td>{touch.channel}</td>
                              <td style={{ wordBreak: "break-word" }}>
                                {touch.utmCampaign || touch.utmSource || "—"}
                              </td>
                              <td style={{ wordBreak: "break-word" }}>
                                <div>{touch.pageUrl || touch.landingPage || "—"}</div>
                                {touch.referrer && (
                                  <small className="text-muted">via {touch.referrer}</small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <AdminPermission>
                        <div className="text-end">
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            disabled={this.state.isErasingAttribution}
                            onClick={this.handleEraseAttributionTimelineClick}
                          >
                            {this.state.isErasingAttribution
                              ? "Erasing…"
                              : "Erase attribution timeline (GDPR Art. 17)"}
                          </Button>
                        </div>
                      </AdminPermission>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
            {this.state.userDetails?.isPaused && (
              <Row>
                <Col className="my-3">
                  <div className="alert alert-warning">
                    <strong>Account Paused</strong>
                    {this.state.userDetails.pausedDate && (
                      <span> since {new Date(this.state.userDetails.pausedDate).toLocaleDateString()}</span>
                    )}
                  </div>
                </Col>
              </Row>
            )}
            <Row>
              <Col className="my-3 col-2">
                {this.state.userDetails?.isPaused ? (
                  <Button color="success" onClick={this.handleUnpauseClick}>
                    Unpause Account
                  </Button>
                ) : (
                  <Button color="warning" onClick={this.handlePauseClick}>
                    Pause Account
                  </Button>
                )}
              </Col>
              <Col className="my-3 col-2">
                <Button color="danger" onClick={this.handleUnsubscribeClick}>
                  {" "}
                  Unsubscribe{" "}
                </Button>
              </Col>
              <Col className="my-3 col-2">
                <Button
                  color="danger"
                  onClick={this.handleUnsubscribeAndNotifyClick}
                >
                  Unsubscribe and notify user
                </Button>
              </Col>
              <Col className="my-3 col-2">
                <Button color="danger" onClick={this.handleDeleteForeverClick}>
                  {" "}
                  Delete forever{" "}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Container>
    );
  }
}

export default UserDetails;
